import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Users, Plus, Trash2, KeyRound, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function UserManagement({ open, onOpenChange }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [showPw, setShowPw] = useState(false);

  // Reset password state
  const [resetEmail, setResetEmail] = useState(null);
  const [resetPw, setResetPw] = useState('');
  const [resetting, setResetting] = useState(false);

  // Fresh start (clear all data) state
  const [showWipe, setShowWipe] = useState(false);
  const [wipeConfirm, setWipeConfirm] = useState('');
  const [wiping, setWiping] = useState(false);

  const handleWipe = async () => {
    if (wipeConfirm !== 'RESET') { toast.error('Type RESET (capital letters) to confirm'); return; }
    setWiping(true);
    try {
      const { data } = await api.post('/admin/reset-data', { confirm: 'RESET' });
      const total = Object.values(data.wiped || {}).reduce((a, b) => a + b, 0);
      toast.success(`All data cleared (${total.toLocaleString()} records) — ready for fresh uploads`);
      setShowWipe(false);
      setWipeConfirm('');
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clear data');
    } finally { setWiping(false); }
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/users');
      setUsers(data);
    } catch (e) { console.error('Failed to fetch users:', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (open) fetchUsers();
  }, [open, fetchUsers]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newEmail.trim() || !newPassword) { toast.error('Email and password required'); return; }
    setCreating(true);
    try {
      await api.post('/users', { email: newEmail.trim(), password: newPassword, name: newName.trim() });
      toast.success(`User ${newEmail} created`);
      setNewEmail(''); setNewPassword(''); setNewName('');
      setShowCreate(false);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create user');
    } finally { setCreating(false); }
  };

  const handleDelete = async (email) => {
    if (!window.confirm(`Delete user ${email}?`)) return;
    try {
      await api.delete(`/users/${encodeURIComponent(email)}`);
      toast.success(`User ${email} deleted`);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleResetPassword = async () => {
    if (!resetPw || resetPw.length < 4) { toast.error('Password must be at least 4 characters'); return; }
    setResetting(true);
    try {
      await api.put(`/users/${encodeURIComponent(resetEmail)}/reset-password`, { password: resetPw });
      toast.success(`Password reset for ${resetEmail}`);
      setResetEmail(null); setResetPw('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset password');
    } finally { setResetting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none sm:rounded-none max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Chivo', sans-serif" }}>
            <Users className="h-5 w-5 text-emerald-600" />
            User Management
          </DialogTitle>
          <DialogDescription>
            Create and manage user accounts. All users get full access.
          </DialogDescription>
        </DialogHeader>

        {/* User list */}
        <div className="space-y-1.5 max-h-[250px] overflow-y-auto">
          {loading ? (
            <div className="py-6 flex justify-center">
              <div className="h-5 w-5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : users.length === 0 ? (
            <p className="text-sm text-zinc-400 py-4 text-center">No users found</p>
          ) : (
            users.map(u => (
              <div key={u.email} className="flex items-center justify-between p-2.5 border border-zinc-100 bg-zinc-50/50 hover:bg-zinc-50">
                <div>
                  <p className="text-sm font-medium text-zinc-900">{u.name || u.email}</p>
                  <p className="text-xs text-zinc-500">{u.email}
                    {u.role === 'admin' && (
                      <span className="ml-1.5 bg-emerald-100 text-emerald-700 border border-emerald-200 text-[9px] font-bold px-1 py-0 uppercase">Admin</span>
                    )}
                  </p>
                </div>
                {u.role !== 'admin' && (
                  <div className="flex items-center gap-1">
                    <Button
                      data-testid={`reset-pw-${u.email}`}
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 rounded-none text-zinc-400 hover:text-zinc-700"
                      onClick={() => { setResetEmail(u.email); setResetPw(''); }}
                      title="Reset password"
                    >
                      <KeyRound className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      data-testid={`delete-user-${u.email}`}
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 rounded-none text-zinc-400 hover:text-red-600"
                      onClick={() => handleDelete(u.email)}
                      title="Delete user"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Reset password inline */}
        {resetEmail && (
          <div className="border border-amber-200 bg-amber-50/50 p-3 space-y-2 animate-fade-in">
            <p className="text-xs font-semibold text-amber-700">Reset password for {resetEmail}</p>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  data-testid="reset-password-input"
                  type={showPw ? 'text' : 'password'}
                  value={resetPw}
                  onChange={(e) => setResetPw(e.target.value)}
                  placeholder="New password (min 4 chars)"
                  className="rounded-none border-zinc-300 h-8 text-sm pr-8 focus:border-emerald-600 focus-visible:ring-0"
                />
                <button onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400">
                  {showPw ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
              </div>
              <Button
                data-testid="confirm-reset-pw"
                size="sm"
                onClick={handleResetPassword}
                disabled={resetting}
                className="rounded-none bg-amber-500 hover:bg-amber-600 text-white h-8 px-3"
              >
                {resetting ? '...' : 'Reset'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setResetEmail(null)} className="rounded-none h-8 px-2">Cancel</Button>
            </div>
          </div>
        )}

        {/* Create user form */}
        {showCreate ? (
          <form onSubmit={handleCreate} className="border border-emerald-200 bg-emerald-50/30 p-3 space-y-2 animate-fade-in">
            <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">New User</p>
            <Input
              data-testid="new-user-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Name"
              className="rounded-none border-zinc-300 h-8 text-sm focus:border-emerald-600 focus-visible:ring-0"
            />
            <Input
              data-testid="new-user-email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Email"
              required
              className="rounded-none border-zinc-300 h-8 text-sm focus:border-emerald-600 focus-visible:ring-0"
            />
            <Input
              data-testid="new-user-password"
              type="text"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Password (min 4 chars)"
              required
              className="rounded-none border-zinc-300 h-8 text-sm font-mono focus:border-emerald-600 focus-visible:ring-0"
            />
            <div className="flex gap-2 pt-1">
              <Button
                data-testid="create-user-submit"
                type="submit"
                disabled={creating}
                size="sm"
                className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-4"
              >
                {creating ? 'Creating...' : 'Create User'}
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setShowCreate(false)} className="rounded-none h-8">Cancel</Button>
            </div>
          </form>
        ) : (
          <Button
            data-testid="add-user-btn"
            variant="outline"
            onClick={() => setShowCreate(true)}
            className="rounded-none border-zinc-300 w-full h-9"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add New User
          </Button>
        )}

        {/* Danger zone: fresh start */}
        <div className="border-t border-zinc-200 pt-3">
          {showWipe ? (
            <div className="border border-red-300 bg-red-50/60 p-3 space-y-2 animate-fade-in">
              <p className="text-xs font-bold text-red-700 flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5" /> This deletes ALL sales, purchases, products, stock and reminders — permanently.
              </p>
              <p className="text-[11px] text-red-600">User accounts and settings are kept. Type <span className="font-mono font-bold">RESET</span> to confirm:</p>
              <div className="flex gap-2">
                <Input
                  data-testid="wipe-confirm-input"
                  value={wipeConfirm}
                  onChange={(e) => setWipeConfirm(e.target.value)}
                  placeholder="Type RESET"
                  className="rounded-none border-red-300 h-8 text-sm font-mono focus:border-red-500 focus-visible:ring-0"
                />
                <Button
                  data-testid="wipe-confirm-btn"
                  size="sm"
                  onClick={handleWipe}
                  disabled={wiping || wipeConfirm !== 'RESET'}
                  className="rounded-none bg-red-600 hover:bg-red-700 text-white h-8 px-3"
                >
                  {wiping ? 'Clearing…' : 'Clear Everything'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setShowWipe(false); setWipeConfirm(''); }} className="rounded-none h-8 px-2">Cancel</Button>
              </div>
            </div>
          ) : (
            <Button
              data-testid="fresh-start-btn"
              variant="outline"
              onClick={() => setShowWipe(true)}
              className="rounded-none w-full h-9 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Fresh Start — Clear All Data
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
